import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { 
  Mail, 
  Send, 
  Loader2, 
  Users, 
  Building2, 
  X,
  CheckCircle2,
  AlertCircle,
  FileText
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_TEMPLATES = [
  { id: "custom", name: "Message personnalisé", description: "Rédigez votre propre message" },
  { id: "rappel_declaration", name: "Rappel de déclaration", description: "Rappel pour la soumission du bilan social" },
  { id: "demande_documents", name: "Demande de documents", description: "Demande de pièces justificatives" },
  { id: "mise_en_demeure", name: "Mise en demeure", description: "Avertissement formel" },
  { id: "information_generale", name: "Information générale", description: "Communication d'informations" },
];

const GROUP_OPTIONS = [
  { id: "all_cabinets", name: "Tous les cabinets actifs", type: "cabinet" },
  { id: "all_enterprises", name: "Toutes les entreprises", type: "enterprise" },
  { id: "enterprises_without_bilan", name: "Entreprises sans bilan cette année", type: "enterprise" },
];

const REGIONS = [
  "Conakry", "Boké", "Kindia", "Mamou", "Labé", "Faranah", "Kankan", "Nzérékoré"
];

export default function SendEmailDialog({ 
  open, 
  onOpenChange, 
  recipients = [], // Pre-selected recipients [{id, email, name, type}]
  recipientType = "cabinet", // "cabinet" or "enterprise"
  allowGroupSelection = false, // Allow sending to groups (admin only)
  title = "Envoyer un email"
}) {
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectionMode, setSelectionMode] = useState("individual"); // "individual" or "group"
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [template, setTemplate] = useState("custom");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Initialize selected recipients when dialog opens
  useEffect(() => {
    if (open && recipients.length > 0) {
      setSelectedRecipients(recipients.map(r => ({
        ...r,
        selected: true
      })));
    }
  }, [open, recipients]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedRecipients([]);
      setSelectionMode("individual");
      setSelectedGroup("");
      setSelectedRegion("");
      setTemplate("custom");
      setSubject("");
      setContent("");
      setResult(null);
    }
  }, [open]);

  // Update subject based on template
  useEffect(() => {
    const templateInfo = EMAIL_TEMPLATES.find(t => t.id === template);
    if (template !== "custom" && templateInfo) {
      setSubject(templateInfo.name);
    }
  }, [template]);

  const toggleRecipient = (recipientId) => {
    setSelectedRecipients(prev => 
      prev.map(r => 
        r.id === recipientId ? { ...r, selected: !r.selected } : r
      )
    );
  };

  const selectAll = () => {
    setSelectedRecipients(prev => prev.map(r => ({ ...r, selected: true })));
  };

  const deselectAll = () => {
    setSelectedRecipients(prev => prev.map(r => ({ ...r, selected: false })));
  };

  const getSelectedCount = () => {
    return selectedRecipients.filter(r => r.selected).length;
  };

  const handleSend = async () => {
    if (selectionMode === "individual") {
      const selected = selectedRecipients.filter(r => r.selected);
      if (selected.length === 0) {
        toast.error("Veuillez sélectionner au moins un destinataire");
        return;
      }
    }

    if (!subject.trim()) {
      toast.error("Veuillez saisir un sujet");
      return;
    }

    if (!content.trim()) {
      toast.error("Veuillez saisir le contenu du message");
      return;
    }

    setSending(true);
    setResult(null);

    try {
      let response;

      if (selectionMode === "group" && allowGroupSelection) {
        // Send to group
        let groupType = selectedGroup;
        let groupValue = null;

        if (selectedGroup === "enterprises_by_region") {
          if (!selectedRegion) {
            toast.error("Veuillez sélectionner une région");
            setSending(false);
            return;
          }
          groupValue = selectedRegion;
        }

        response = await axios.post(`${API_URL}/emails/send-group`, {
          group_type: groupType,
          group_value: groupValue,
          subject,
          content,
          template
        });
      } else {
        // Send to individual recipients
        const selected = selectedRecipients.filter(r => r.selected);
        response = await axios.post(`${API_URL}/emails/send`, {
          recipients: selected.map(r => ({
            id: r.id,
            email: r.email,
            name: r.name,
            type: r.type || recipientType
          })),
          subject,
          content,
          template
        });
      }

      setResult(response.data);
      
      if (response.data.fail_count === 0) {
        toast.success(`${response.data.success_count} email(s) envoyé(s) avec succès`);
      } else if (response.data.success_count > 0) {
        toast.warning(`${response.data.success_count} envoyé(s), ${response.data.fail_count} échec(s)`);
      } else {
        toast.error("Échec de l'envoi des emails");
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Envoyez un message aux {recipientType === "cabinet" ? "cabinets" : "entreprises"} sélectionnés
          </DialogDescription>
        </DialogHeader>

        {result ? (
          // Show result
          <div className="py-6 space-y-4">
            <div className={`p-4 rounded-lg ${result.fail_count === 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center gap-3">
                {result.fail_count === 0 ? (
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                )}
                <div>
                  <p className="font-semibold text-lg">
                    {result.fail_count === 0 ? "Envoi réussi !" : "Envoi partiel"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {result.success_count} email(s) envoyé(s)
                    {result.fail_count > 0 && `, ${result.fail_count} échec(s)`}
                  </p>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // Show form
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Selection Mode (if group selection allowed) */}
            {allowGroupSelection && (
              <div className="space-y-2">
                <Label>Mode de sélection</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={selectionMode === "individual" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectionMode("individual")}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Individuel
                  </Button>
                  <Button
                    type="button"
                    variant={selectionMode === "group" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectionMode("group")}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Par groupe
                  </Button>
                </div>
              </div>
            )}

            {/* Group Selection */}
            {selectionMode === "group" && allowGroupSelection && (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Groupe de destinataires</Label>
                  <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un groupe..." />
                    </SelectTrigger>
                    <SelectContent>
                      {GROUP_OPTIONS.map(group => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="enterprises_by_region">
                        Entreprises par région
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {selectedGroup === "enterprises_by_region" && (
                  <div className="space-y-2">
                    <Label>Région</Label>
                    <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une région..." />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map(region => (
                          <SelectItem key={region} value={region}>
                            {region}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Individual Recipients */}
            {selectionMode === "individual" && selectedRecipients.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Destinataires ({getSelectedCount()} sélectionné(s))</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={selectAll}>
                      Tout sélectionner
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={deselectAll}>
                      Tout désélectionner
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-32 border rounded-lg p-2">
                  <div className="space-y-2">
                    {selectedRecipients.map(recipient => (
                      <div 
                        key={recipient.id}
                        className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded"
                      >
                        <Checkbox
                          checked={recipient.selected}
                          onCheckedChange={() => toggleRecipient(recipient.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{recipient.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{recipient.email}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {recipient.type === "cabinet" ? "Cabinet" : "Entreprise"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Template Selection */}
            <div className="space-y-2">
              <Label>Modèle de message</Label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATES.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex flex-col">
                        <span>{t.name}</span>
                        <span className="text-xs text-muted-foreground">{t.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Sujet *</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Sujet de l'email..."
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content">Message *</Label>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Rédigez votre message..."
                rows={6}
              />
            </div>
          </div>
        )}

        {!result && (
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
              Annuler
            </Button>
            <Button onClick={handleSend} disabled={sending} className="bg-primary">
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
